import React, { useEffect, useState, useRef, useCallback } from "react";
import { User2, ChevronDown, ChevronUp, Package } from "lucide-react";
import { Spin, message, Pagination } from "antd";
import { motion, AnimatePresence } from "framer-motion";
import inventory from "../../api/inventoryApi";
import usersApi from "../../api/usersApi";
import InventoryCharts from "../../components/Charts/InventoryCharts/InventoryCharts";

const IMAGE_BASE_URL = "https://api.duotetra.quest/uploads/gems/";

const CARD_HEIGHT = 130;
const HEADER_HEIGHT = 70;
const PAGINATION_HEIGHT = 70;

function cn(...c) {
    return c.filter(Boolean).join(" ");
}

const shellClass =
    "relative overflow-hidden rounded-2xl border border-white/10 bg-white/[0.04] backdrop-blur-xl shadow-[0_18px_60px_-30px_rgba(0,0,0,0.9)]";

const cardShell =
    "rounded-xl border border-white/10 bg-white/[0.04] p-2 shadow-[0_12px_40px_-24px_rgba(0,0,0,0.85)] transition-colors hover:border-white/[0.16]";

export default function UserInventoryPage() {
    const [inventories, setInventories] = useState([]);
    const [loading, setLoading] = useState(false);
    const [userProfiles, setUserProfiles] = useState({});
    const [gemTypes, setGemTypes] = useState({});
    const [loadingUsers, setLoadingUsers] = useState({});
    const [total, setTotal] = useState(0);
    const [page, setPage] = useState(1);
    const [limit, setLimit] = useState(10);
    const [showCharts, setShowCharts] = useState(true);

    const containerRef = useRef(null);
    const requestIdRef = useRef(0);
    const isMountedRef = useRef(false);

    useEffect(() => {
        isMountedRef.current = true;
        return () => {
            isMountedRef.current = false;
            requestIdRef.current += 1;
        };
    }, []);

    const calculateOptimalLimit = useCallback(() => {
        if (typeof window === "undefined") return;

        const windowHeight = window.innerHeight;
        const availableHeight = windowHeight - HEADER_HEIGHT - PAGINATION_HEIGHT;

        const rows = Math.floor(availableHeight / CARD_HEIGHT);

        let cols = 1;
        if (window.innerWidth >= 1280) cols = 4;
        else if (window.innerWidth >= 1024) cols = 3;
        else if (window.innerWidth >= 768) cols = 2;
        else cols = 1;

        const optimalLimit = Math.max(rows * cols, 8);

        const options = [10, 20, 50, 100];
        const closest = options.reduce((prev, curr) =>
            Math.abs(curr - optimalLimit) < Math.abs(prev - optimalLimit) ? curr : prev
        );

        setLimit((prev) => (prev === closest ? prev : closest));
    }, []);

    const fetchGemTypes = useCallback(async () => {
        try {
            const gems = await inventory.gemCatalogApi.getAllGems();
            const gemsMap = {};
            gems.forEach((gem) => {
                gemsMap[gem.id] = gem;
            });
            if (isMountedRef.current) setGemTypes(gemsMap);
        } catch (e) {
            console.error("Ошибка при загрузке типов гемов:", e);
        }
    }, []);

    const fetchUserProfiles = useCallback(
        async (inventoryData) => {
            const userIds = inventoryData.map((item) => item.userId).filter(Boolean);
            const uniqueIds = [...new Set(userIds)];
            const missingIds = uniqueIds.filter((id) => !userProfiles[id] && !loadingUsers[id]);

            if (missingIds.length === 0) return;

            setLoadingUsers((prev) => {
                const updated = { ...prev };
                missingIds.forEach((id) => (updated[id] = true));
                return updated;
            });

            try {
                const profiles = await Promise.allSettled(
                    missingIds.map(async (id) => {
                        try {
                            const profile = await usersApi.getProfile(id);
                            return { id, profile };
                        } catch (error) {
                            console.error(`Ошибка при загрузке профиля пользователя ${id}:`, error);
                            return { id, profile: null };
                        }
                    })
                );

                if (!isMountedRef.current) return;

                setUserProfiles((prev) => {
                    const updated = { ...prev };
                    profiles.forEach((result) => {
                        if (result.status === "fulfilled" && result.value.profile) {
                            updated[result.value.id] = result.value.profile;
                        }
                    });
                    return updated;
                });
            } catch (e) {
                console.error("Ошибка при загрузке профилей:", e);
            } finally {
                if (!isMountedRef.current) return;
                setLoadingUsers((prev) => {
                    const updated = { ...prev };
                    missingIds.forEach((id) => delete updated[id]);
                    return updated;
                });
            }
        },
        [userProfiles, loadingUsers]
    );

    const fetchInventory = useCallback(async () => {
        const myRequestId = ++requestIdRef.current;

        setLoading(true);

        try {
            const response = await inventory.inventoryApi.getAllInventories(page, limit);

            if (!isMountedRef.current || myRequestId !== requestIdRef.current) return;

            const items = response?.data || [];
            setInventories(items);
            setTotal(response?.total || 0);

            if (items.length > 0) fetchUserProfiles(items);
        } catch (e) {
            if (!isMountedRef.current || myRequestId !== requestIdRef.current) return;
            console.error("Ошибка при загрузке инвентаря:", e);
            message.error("Ошибка при загрузке инвентаря");
        } finally {
            if (!isMountedRef.current || myRequestId !== requestIdRef.current) return;
            setLoading(false);
        }
    }, [page, limit, fetchUserProfiles]);

    useEffect(() => {
        fetchGemTypes();
    }, [fetchGemTypes]);

    useEffect(() => {
        calculateOptimalLimit();
        const handleResize = () => calculateOptimalLimit();
        window.addEventListener("resize", handleResize);
        return () => window.removeEventListener("resize", handleResize);
    }, [calculateOptimalLimit]);

    useEffect(() => {
        fetchInventory();
    }, [fetchInventory]);

    const getUserDisplayName = (userId, profile) => {
        if (profile?.username) return profile.username;
        if (profile?.firstName || profile?.lastName) {
            return `${profile.firstName || ""} ${profile.lastName || ""}`.trim();
        }
        return `User #${userId}`;
    };

    const getUserAvatar = (profile) => {
        if (profile?.avatar) return `https://api.duotetra.quest${profile.avatar}`;
        return null;
    };

    const totalPages = Math.max(1, Math.ceil(total / limit) || 1);

    if (loading && inventories.length === 0) {
        return (
            <div className="flex min-h-[calc(100dvh-1.5rem)] flex-1 flex-col gap-4 p-4 text-white sm:p-5">
                <section className={cn(shellClass, "flex min-h-[280px] flex-1 items-center justify-center p-12")}>
                    <Spin size="large" className="[&_.ant-spin-dot-item]:!bg-emerald-400" />
                </section>
            </div>
        );
    }

    return (
        <div className="flex min-h-[calc(100dvh-1.5rem)] flex-1 flex-col gap-4 overflow-hidden p-4 text-white sm:p-5">
            <section className={cn(shellClass, "shrink-0 p-4 sm:p-5")}>
                <div className="pointer-events-none absolute -right-10 -top-10 h-36 w-36 rounded-full bg-sky-500/10 blur-3xl" />
                <div className="relative flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
                    <div className="flex items-center gap-3">
                        <span className="flex h-11 w-11 shrink-0 items-center justify-center rounded-xl border border-white/10 bg-white/[0.06]">
                            <Package className="h-5 w-5 text-sky-300/90" strokeWidth={2} />
                        </span>
                        <div>
                            <h1 className="flex items-center gap-2 text-lg font-semibold tracking-tight text-white/95 sm:text-xl">
                                <User2 size={20} className="text-emerald-300/90" />
                                Инвентарь пользователей
                            </h1>
                            <p className="text-[12px] text-white/45">Кристаллы по аккаунтам, пагинация и обзор</p>
                        </div>
                    </div>
                    <button
                        type="button"
                        onClick={() => setShowCharts(!showCharts)}
                        className={cn(
                            "inline-flex items-center gap-2 rounded-xl border border-white/10 bg-white/[0.06] px-3 py-2 text-[12px] font-medium text-white/70 transition-colors hover:border-white/20 hover:bg-white/10 hover:text-white/90"
                        )}
                    >
                        {showCharts ? (
                            <>
                                <ChevronUp size={16} />
                                Скрыть статистику
                            </>
                        ) : (
                            <>
                                <ChevronDown size={16} />
                                Показать статистику
                            </>
                        )}
                    </button>
                </div>
            </section>

            <AnimatePresence>
                {showCharts && (
                    <motion.div
                        initial={{ height: 0, opacity: 0 }}
                        animate={{ height: "auto", opacity: 1 }}
                        exit={{ height: 0, opacity: 0 }}
                        transition={{ duration: 0.3 }}
                        className="overflow-hidden"
                    >
                        <section className={cn(shellClass, "p-3 sm:p-4")}>
                            <InventoryCharts />
                        </section>
                    </motion.div>
                )}
            </AnimatePresence>

            {total > 0 && (
                <div
                    className={cn(
                        shellClass,
                        "flex shrink-0 flex-col gap-3 px-4 py-3 sm:flex-row sm:items-center sm:justify-between sm:px-5"
                    )}
                >
                    <div className="flex flex-col gap-1 sm:flex-row sm:items-center sm:gap-4">
                        <span className="text-[13px] text-white/50">
                            Стр. {page} из {totalPages}
                        </span>
                        <span className="text-[12px] text-white/35">
                            Записей: {total} · на странице: {limit}
                        </span>
                    </div>
                    <Pagination
                        current={page}
                        pageSize={limit}
                        total={total}
                        onChange={(newPage) => {
                            setPage(newPage);
                            if (containerRef.current) {
                                containerRef.current.scrollTo({ top: 0, behavior: "smooth" });
                            }
                        }}
                        onShowSizeChange={(current, size) => {
                            setLimit(size);
                            setPage(1);
                            if (containerRef.current) {
                                containerRef.current.scrollTo({ top: 0, behavior: "smooth" });
                            }
                        }}
                        showSizeChanger
                        pageSizeOptions={["10", "20", "50", "100"]}
                        showLessItems
                        responsive
                        showQuickJumper={total > 100}
                        showTotal={(t, range) => `${range[0]}-${range[1]} из ${t}`}
                        className="pagination-custom !mb-0 flex flex-wrap justify-end"
                        size="small"
                    />
                </div>
            )}

            <section className={cn(shellClass, "relative flex min-h-0 flex-1 flex-col overflow-hidden")}>
                {loading && inventories.length > 0 && (
                    <div className="absolute inset-0 z-10 flex items-center justify-center rounded-2xl bg-[#070A12]/45 backdrop-blur-sm">
                        <Spin size="large" className="[&_.ant-spin-dot-item]:!bg-emerald-400" />
                    </div>
                )}
                <div ref={containerRef} className="min-h-0 flex-1 overflow-y-auto p-3 sm:p-4">
                    {inventories.length === 0 ? (
                        <div className="flex flex-col items-center py-16 text-white/45">
                            <Package className="mb-2 h-12 w-12 opacity-40" />
                            <p className="text-[13px]">Инвентарь пуст</p>
                        </div>
                    ) : (
                        <div className="grid grid-cols-1 gap-3 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 2xl:grid-cols-5">
                            {inventories.map((item, index) => {
                                const profile = userProfiles[item.userId];
                                const isLoadingUser = loadingUsers[item.userId];

                                return (
                                    <motion.div
                                        key={`${item.userId}-${page}-${limit}`}
                                        initial={{ opacity: 0, y: 16 }}
                                        animate={{ opacity: 1, y: 0 }}
                                        transition={{ duration: 0.28, delay: index * 0.04 }}
                                        className={cardShell}
                                    >
                                        <div className="mb-2 flex items-center gap-2 border-b border-white/10 pb-2">
                                            {isLoadingUser ? (
                                                <div className="h-6 w-6 shrink-0 animate-pulse rounded-full bg-white/10" />
                                            ) : getUserAvatar(profile) ? (
                                                <img
                                                    src={getUserAvatar(profile)}
                                                    alt=""
                                                    className="h-6 w-6 shrink-0 rounded-full border border-white/10 object-cover"
                                                    onError={(e) => {
                                                        e.currentTarget.style.display = "none";
                                                        if (e.currentTarget.nextSibling) {
                                                            e.currentTarget.nextSibling.style.display = "flex";
                                                        }
                                                    }}
                                                />
                                            ) : null}

                                            {!getUserAvatar(profile) && (
                                                <div
                                                    className="flex h-6 w-6 shrink-0 items-center justify-center rounded-full border border-white/10 bg-white/[0.08] text-[10px] font-semibold text-white/50"
                                                    style={{
                                                        display: getUserAvatar(profile) ? "none" : "flex",
                                                    }}
                                                >
                                                    {isLoadingUser
                                                        ? "…"
                                                        : profile?.username?.[0]?.toUpperCase() ||
                                                          String(item.userId || "?")[0]}
                                                </div>
                                            )}

                                            <div className="min-w-0 flex-1">
                                                <div className="truncate text-[12px] font-semibold text-white/90">
                                                    {isLoadingUser ? (
                                                        <div className="h-2.5 w-16 animate-pulse rounded bg-white/10" />
                                                    ) : (
                                                        getUserDisplayName(item.userId, profile)
                                                    )}
                                                </div>
                                                <div className="text-[10px] text-white/40">ID: {item.userId}</div>
                                            </div>
                                        </div>

                                        <div>
                                            <div className="mb-1.5 text-[10px] font-medium uppercase tracking-wide text-white/40">
                                                Гемы ({item.gems?.length || 0})
                                            </div>

                                            {item.gems && item.gems.length > 0 ? (
                                                <div className="grid grid-cols-4 gap-1">
                                                    {item.gems.map((gem, gemIndex) => {
                                                        const gemType = gemTypes[gem.gemId];
                                                        const count = parseFloat(gem.count) || 0;
                                                        const hasPosition =
                                                            gemType?.position !== undefined &&
                                                            gemType?.position !== null;
                                                        const gemImage = hasPosition
                                                            ? gemType?.frontImage
                                                            : gemType?.backImage || gemType?.frontImage;

                                                        return (
                                                            <div
                                                                key={`${gem.gemId}-${gemIndex}`}
                                                                className="group relative aspect-square cursor-pointer rounded-lg border border-white/10 bg-black/25 transition-colors hover:border-emerald-400/45 hover:bg-white/[0.04]"
                                                                title={gemType?.name || `Gem #${gem.gemId}`}
                                                            >
                                                                {gemImage ? (
                                                                    <img
                                                                        src={`${IMAGE_BASE_URL}${gemImage}`}
                                                                        alt=""
                                                                        className="h-full w-full object-contain p-0.5"
                                                                        onError={(e) => {
                                                                            e.currentTarget.style.display = "none";
                                                                            if (e.currentTarget.nextSibling) {
                                                                                e.currentTarget.nextSibling.style.display =
                                                                                    "flex";
                                                                            }
                                                                        }}
                                                                    />
                                                                ) : (
                                                                    <div className="flex h-full w-full items-center justify-center rounded text-[7px] text-white/35">
                                                                        #{gem.gemId}
                                                                    </div>
                                                                )}

                                                                {count > 0 && (
                                                                    <div className="absolute bottom-0 left-0 right-0 rounded-b bg-[#070A12]/85 py-[1px] text-center text-[7px] font-semibold leading-tight text-emerald-300/95">
                                                                        {count > 1 ? count.toFixed(1) : count.toFixed(4)}
                                                                    </div>
                                                                )}

                                                                <div className="absolute inset-0 z-10 flex flex-col items-center justify-center rounded bg-[#070A12]/90 p-0.5 opacity-0 transition-opacity group-hover:opacity-100">
                                                                    <div className="mb-0.5 text-center text-[8px] font-medium text-white/95">
                                                                        {gemType?.name || `Gem #${gem.gemId}`}
                                                                    </div>
                                                                    {hasPosition && (
                                                                        <div className="text-[7px] text-white/45">
                                                                            Поз: {gemType.position}
                                                                        </div>
                                                                    )}
                                                                    <div className="mt-0.5 text-[8px] font-semibold text-emerald-300/95">
                                                                        {count > 0 ? count.toFixed(4) : "0.0000"}
                                                                    </div>
                                                                </div>
                                                            </div>
                                                        );
                                                    })}
                                                </div>
                                            ) : (
                                                <div className="py-2 text-center text-[10px] text-white/35">
                                                    Нет гемов
                                                </div>
                                            )}
                                        </div>
                                    </motion.div>
                                );
                            })}
                        </div>
                    )}
                </div>
            </section>
        </div>
    );
}
